import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { API_CONFIG } from '../config/api';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import SnackbarAlert from '../components/SnackbarAlert';
import { adminService, User } from '../services/adminService';
import { createDecipheriv } from 'crypto';
import { COLORS } from '../config/colors';

export const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userDialog, setUserDialog] = useState<{ open: boolean; user?: User }>({ open: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as any });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    role: 'user',
    isActive: true,
  });
  const [formLoading, setFormLoading] = useState(false);
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);
      const createdBy = localStorage.getItem('user_email') || 'unknown';

      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Fetching users from admin API...');

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.USERS.LIST}?page=1&size=100&createdBy=${createdBy}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('Response status:', response.status);
      console.log('Response OK:', response.ok);

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Response body:', responseText);

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      const userData = await response.json();
      console.log('Users API response:', userData);

      // Ensure userData is an array
      const userList = userData?.total_count || 0;
      setUsers(userData.users);

      if (userList.length === 0) {
        setError('No admin users found');
      } else {
        console.log('Fetched users:', userList);
      }
    } catch (err: any) {
      console.error('Detailed error:', err);
      const errorMessage = err.message || 'Failed to load users';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: `Failed to load users: ${errorMessage}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if user is admin
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user?.role]);

  const handleAddUser = () => {
    setFormData({
      username: '',
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      role: 'user',
      isActive: true,
    });
    setUserDialog({ open: true });
  };

  const handleEditUser = (user: User) => {
    setFormData({
      username: user.username,
      firstname: user.firstname || '',
      lastname: user.lastname || '',
      email: user.email,
      password: '', // Don't pre-fill password for editing
      role: user.role,
      isActive: user.isActive,
    });
    setUserDialog({ open: true, user });
  };

  const handleUpdateUser = async () => {
    setFormLoading(true);
    try {
      // Validate required fields
      if (!formData.username || !formData.email) {
        throw new Error('Username and email are required');
      }

      // Prepare user data for API (exclude password if empty for updates)
      const userData: any = {
        username: formData.username,
        firstName: formData.firstname,
        lastName: formData.lastname,
        role: formData.role,
        isActive: formData.isActive,
      };

      // Only include password if it's provided
      if (formData.password) {
        userData.password = formData.password;
      }

      console.log('Updating user with data:', userData);

      // Update user via admin service
      const updatedUser = await adminService.updateUser(userDialog.user!.email, userData);

      // Update user in local state
      setUsers(prevUsers =>
        prevUsers.map(user => (user.username === userDialog.user?.username ? updatedUser : user))
      );

      // Close dialog and show success message
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: 'User updated successfully',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Error updating user:', error);
      setSnackbar({
        open: true,
        message: `Failed to update user: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    setFormLoading(true);
    try {
      console.log('Deleting user with email:', user.email);

      await adminService.deleteUser(user.email);

      setUsers(prevUsers => prevUsers.filter(u => u.email !== user.email));

      setSnackbar({
        open: true,
        message: `User ${user.username} deleted successfully`,
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete user: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setUserDialog({ open: false });
    setFormData({
      username: '',
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      role: 'user',
      isActive: true,
    });
  };

  const handleCreateUser = async () => {
    setFormLoading(true);
    try {
      // Validate required fields
      if (!formData.username || !formData.email || !formData.password) {
        throw new Error('Username, email, and password are required');
      }

      // Prepare user data for API
      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        firstname: formData.firstname,
        lastname: formData.lastname,
        role: formData.role,
        isActive: formData.isActive,
        createdBy: localStorage.getItem('user_email') || 'admin',
      };

      console.log('Creating user with data:', userData);

      // Create user via admin service
      const newUser = await adminService.createUser(userData);

      // Add new user to the list (the API should return a proper User object)
      setUsers(prevUsers => [...prevUsers, newUser]);

      // Close dialog and show success message
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: 'User created successfully',
        severity: 'success',
      });

      // Refresh users list
      fetchUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      setSnackbar({
        open: true,
        message: `Failed to create user: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRefreshUsers = () => {
    fetchUsers();
  };

  const getRoleChipColor = (role: string) => {
    const colors: Record<string, any> = {
      admin: 'error',
      hr: 'primary',
      sme: 'info',
      panelist: 'success',
      candidate: 'default',
      user: 'secondary',
    };
    return colors[role] || 'default';
  };

  const getStatusChipColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      !searchTerm ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.firstname && user.firstname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.lastname && user.lastname.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesRole = !filterRole || user.role === filterRole;
    const matchesStatus =
      !filterStatus || (filterStatus === 'active' ? user.isActive : !user.isActive);

    return matchesSearch && matchesRole && matchesStatus;
  });

  // Calculate stats
  const stats = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.isActive).length,
    hrUsers: users.filter(u => u.role === 'hr').length,
    smeUsers: users.filter(u => u.role === 'sme').length,
    adminUsers: users.filter(u => u.role === 'admin').length,
  };

  return (
    <Box sx={{ minHeight: '100vh', background:  COLORS.GRAY_100 , py: 3, px: 2 }}>
      <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              color: COLORS.PRIMARY_BLUE_DARK,
              mb: 1,
              letterSpacing: 0.2,
            }}
          >
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage system users, assign roles, and control access permissions.
          </Typography>
        </Box>
        {/* Stats Chips */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.PRIMARY_BLUE_DARK, fontWeight: 600 }}>
                  {stats.totalUsers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.SUCCESS_DARK, fontWeight: 600 }}>
                  {stats.activeUsers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.PRIMARY, fontWeight: 600 }}>
                  {stats.hrUsers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  HR Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.SUCCESS_DARK, fontWeight: 600 }}>
                  {stats.smeUsers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  SME Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.ERROR_DARK, fontWeight: 600 }}>
                  {stats.adminUsers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Admin Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filter Bar */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by username, email, or name..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Role"
                value={filterRole}
                onChange={e => setFilterRole(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="hr">HR</MenuItem>
                <MenuItem value="sme">SME</MenuItem>
                <MenuItem value="panelist">Panelist</MenuItem>
                <MenuItem value="candidate">Candidate</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshUsers}
                  disabled={loading}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    borderColor: 'var(--primary-blue)',
                    color: 'var(--primary-blue)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      borderColor: 'var(--primary-blue)',
                    },
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    backgroundColor: 'var(--primary-blue)',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      backgroundColor: 'var(--primary-blue)',
                      filter: 'brightness(0.9)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                    },
                  }}
                >
                  Add User
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Users Table */}
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading users...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      {users.length === 0
                        ? 'No users found. Click "Add User" to create a new user.'
                        : 'No users match your search criteria.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map(user => (
                  <TableRow key={user.username} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: 'var(--primary-blue)' }}
                      >
                        {user.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.firstname && user.lastname
                          ? `${user.firstname} ${user.lastname}`
                          : user.fullname || user.firstname || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role.toUpperCase()}
                        color={getRoleChipColor(user.role)}
                        size="small"
                        sx={{
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          width: '80px',
                          textAlign: 'center',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={getStatusChipColor(user.isActive)}
                        size="small"
                        sx={{
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.lastLogin ? formatDate(user.lastLogin) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                            sx={{
                              color: '#64748b',
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: 1,
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: '#cbd5e1',
                                color: '#475569',
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user)}
                            sx={{
                              color: '#64748b',
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: 1,
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: '#fef2f2',
                                borderColor: '#fecaca',
                                color: '#dc2626',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* User Dialog */}
      <Dialog open={userDialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{userDialog.user ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              fullWidth
              value={formData.username}
              onChange={e => handleFormInputChange('username', e.target.value)}
              required
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  value={formData.firstname}
                  onChange={e => handleFormInputChange('firstname', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  value={formData.lastname}
                  onChange={e => handleFormInputChange('lastname', e.target.value)}
                />
              </Grid>
            </Grid>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={e => handleFormInputChange('email', e.target.value)}
              required
            />
            {!userDialog.user && (
              <TextField
                label="Password"
                type="password"
                fullWidth
                value={formData.password}
                onChange={e => handleFormInputChange('password', e.target.value)}
                required
              />
            )}
            <TextField
              label="Role"
              select
              fullWidth
              value={formData.role}
              onChange={e => handleFormInputChange('role', e.target.value)}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="hr">HR</MenuItem>
              <MenuItem value="sme">SME</MenuItem>
              <MenuItem value="panelist">Panelist</MenuItem>
              <MenuItem value="candidate">Candidate</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </TextField>
            <TextField
              label="Status"
              select
              fullWidth
              value={formData.isActive ? 'active' : 'inactive'}
              onChange={e => handleFormInputChange('isActive', e.target.value === 'active')}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={userDialog.user ? handleUpdateUser : handleCreateUser}
            disabled={
              formLoading ||
              !formData.username ||
              !formData.email ||
              (!userDialog.user && !formData.password)
            }
          >
            {formLoading ? <CircularProgress size={20} /> : userDialog.user ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <SnackbarAlert snackbar={snackbar} setSnackbar={setSnackbar} />
    </Box>
  );
};

export default AdminDashboardPage;
